/**
 * Login Page
 *
 * Authentication page for audit log administrators.
 * Supports standard login and NEW_PASSWORD_REQUIRED challenge flow.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Shield, Loader2 } from 'lucide-react';
import { useAuth } from '@/state/auth-context';
import { cognitoPasswordField } from '@/lib/auth-schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Login Form Schema
 * Validates email and password fields
 */
const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

/**
 * New Password Form Schema
 * Validates new password with requirements
 */
const newPasswordSchema = z
  .object({
    newPassword: cognitoPasswordField,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type NewPasswordFormData = z.infer<typeof newPasswordSchema>;

/**
 * Login Page Component
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, respondToNewPassword, isAuthenticated, state } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challengeData, setChallengeData] = useState<{
    username: string; // Backend expects 'username' field (will contain email value)
    session: string;
  } | null>(null);

  // Get the intended destination from location state, default to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  // Track state.tokens and state.user to detect auth changes
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, state.tokens, state.user, navigate, from]);

  // Login form
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // New password form
  const newPasswordForm = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  /**
   * Handle Login Submit
   */
  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await login(data);

      // Check if password change is required
      if (response.challengeName === 'NEW_PASSWORD_REQUIRED' && response.session) {
        setChallengeData({
          username: data.email, // Backend expects 'username' field for password challenge
          session: response.session,
        });
      } else {
        // Successful login - useEffect will handle navigation automatically
        // when isAuthenticated becomes true after state update completes
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Login failed. Please check your credentials and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle New Password Submit
   */
  const onNewPasswordSubmit = async (data: NewPasswordFormData) => {
    if (!challengeData) {
      setError('Session expired. Please log in again.');
      setChallengeData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await respondToNewPassword({
        username: challengeData.username,
        newPassword: data.newPassword,
        session: challengeData.session,
      });

      // Successful password change - useEffect will handle navigation
      // when isAuthenticated becomes true after state update completes
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Password change failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cancel Password Change
   * Returns to login form
   */
  const handleCancelPasswordChange = () => {
    setChallengeData(null);
    setError(null);
    loginForm.reset();
    newPasswordForm.reset();
  };

  // Show new password form if challenge is active
  if (challengeData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <Shield className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">Change Password Required</CardTitle>
            <CardDescription>You must change your password before continuing</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={newPasswordForm.handleSubmit(onNewPasswordSubmit)}
              className="space-y-4"
            >
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <PasswordInput
                  id="newPassword"
                  {...newPasswordForm.register('newPassword')}
                  disabled={isLoading}
                />
                {newPasswordForm.formState.errors.newPassword && (
                  <p className="text-sm text-red-600">
                    {newPasswordForm.formState.errors.newPassword.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Must be 8+ characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <PasswordInput
                  id="confirmPassword"
                  {...newPasswordForm.register('confirmPassword')}
                  disabled={isLoading}
                />
                {newPasswordForm.formState.errors.confirmPassword && (
                  <p className="text-sm text-red-600">
                    {newPasswordForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelPasswordChange}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading} className="flex-1">
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Standard login form
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Admin Portal</CardTitle>
          <CardDescription>
            Sign in with your admin credentials to access the admin dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...loginForm.register('email')}
                disabled={isLoading}
                autoComplete="email"
              />
              {loginForm.formState.errors.email && (
                <p className="text-sm text-red-600">{loginForm.formState.errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                placeholder="Enter your password"
                {...loginForm.register('password')}
                disabled={isLoading}
                autoComplete="current-password"
              />
              {loginForm.formState.errors.password && (
                <p className="text-sm text-red-600">
                  {loginForm.formState.errors.password.message}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>

            <div className="text-center text-sm">
              <Link to="/forgot-password" className="text-primary hover:underline">
                Forgot password?
              </Link>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              <p>Protected by AWS Cognito</p>
              <p className="mt-1">Admin access only</p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
